import { gql } from "@apollo/client";
import { useCallback } from "react";
import { client } from "../apolloClient";
import { useMutation } from "@apollo/client";

/**
 * useBusinessSemanticLayer custom hook for business semantic layer operations.
 * - Provides fetch, update, and delete for business semantic layers via Apollo Client and GraphQL.
 * - Used by useBusinessDefinitionViewModel for the Business Definition view.
 */

const BUSINESS_SEMANTIC_LAYER_QUERIES = {
  GET_BUSINESS_SEMANTIC_LAYERS: gql`
    query GetBusinessSemanticLayers($userDbId: Int!) {
      getBusinessSemanticLayers(user_db_id: $userDbId) {
        business_semantic_layer_id
        concept_type
        concept_key
        concept_text
        definition
        created_on
        updated_on
      }
    }
  `,

  UPDATE_BUSINESS_SEMANTIC_LAYER: gql`
    mutation UpdateBusinessSemanticLayer($businessSemanticLayerId: ID!, $conceptType: BusinessSemanticLayerConceptType, $conceptKey: String, $conceptText: String, $definition: JSON) {
      updateBusinessSemanticLayer(
        business_semantic_layer_id: $businessSemanticLayerId
        concept_type: $conceptType
        concept_key: $conceptKey
        concept_text: $conceptText
        definition: $definition
      ) {
        business_semantic_layer_id
        user_db_id
        concept_type
        concept_key
        concept_text
        definition
        is_active
        created_on
        updated_on
      }
    }
  `,

  ADD_BUSINESS_SEMANTIC_LAYER: gql`
    mutation AddBusinessSemanticLayer($userDbId: Int!, $conceptType: BusinessSemanticLayerConceptType!, $conceptKey: String!, $conceptText: String!, $definition: JSON!) {
      addBusinessSemanticLayer(
        user_db_id: $userDbId
        concept_type: $conceptType
        concept_key: $conceptKey
        concept_text: $conceptText
        definition: $definition
      ) {
        business_semantic_layer_id
        user_db_id
        concept_type
        concept_key
        concept_text
        definition
        is_active
        created_on
        updated_on
      }
    }
  `,

  DELETE_BUSINESS_SEMANTIC_LAYER: gql`
    mutation DeleteBusinessSemanticLayer($id: ID!) {
      deleteBusinessSemanticLayer(business_semantic_layer_id: $id)
    }
  `,
};

export const useBusinessSemanticLayer = () => {
  const [addMutation] = useMutation(BUSINESS_SEMANTIC_LAYER_QUERIES.ADD_BUSINESS_SEMANTIC_LAYER);
  const [updateMutation] = useMutation(BUSINESS_SEMANTIC_LAYER_QUERIES.UPDATE_BUSINESS_SEMANTIC_LAYER);
  const [deleteMutation] = useMutation(BUSINESS_SEMANTIC_LAYER_QUERIES.DELETE_BUSINESS_SEMANTIC_LAYER);

  /**
   * Fetches business semantic layers for a user database.
   * @param {number} userDbId
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getBusinessSemanticLayers = useCallback(async (userDbId) => {
    if (!userDbId) return { success: true, data: [] };
    try {
      const { data } = await client.query({
        query: BUSINESS_SEMANTIC_LAYER_QUERIES.GET_BUSINESS_SEMANTIC_LAYERS,
        variables: { userDbId },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data?.getBusinessSemanticLayers ?? [] };
    } catch (error) {
      console.error("Error fetching business semantic layers:", error);
      return { success: false, error: error?.message ?? "Failed to fetch" };
    }
  }, []);

  /**
   * Adds a new business semantic layer.
   * @param {Object} payload - { userDbId, conceptType, conceptKey, conceptText, definition }
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const addBusinessSemanticLayer = useCallback(async (payload) => {
    try {
      const { data, errors } = await addMutation({
        variables: {
          userDbId: payload.userDbId,
          conceptType: payload.conceptType,
          conceptKey: payload.conceptKey,
          conceptText: payload.conceptText ?? "",
          definition: payload.definition,
        },
      });

      if (errors?.length > 0) {
        throw new Error(errors[0].message);
      }

      if (!data?.addBusinessSemanticLayer) {
        throw new Error("Failed to add business definition");
      }

      return { success: true, data: data.addBusinessSemanticLayer };
    } catch (error) {
      if (error.graphQLErrors?.length > 0) {
        throw new Error(error.graphQLErrors[0].message);
      }
      if (error.networkError?.result?.errors?.length > 0) {
        throw new Error(error.networkError.result.errors[0].message);
      }
      throw error;
    }
  }, [addMutation]);

  /**
   * Updates a business semantic layer.
   * @param {Object} payload - { businessSemanticLayerId, conceptType, conceptKey, conceptText, definition }
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateBusinessSemanticLayer = useCallback(async (payload) => {
    try {
      const { data, errors } = await updateMutation({
        variables: {
          businessSemanticLayerId: payload.businessSemanticLayerId,
          conceptType: payload.conceptType ?? null,
          conceptKey: payload.conceptKey ?? null,
          conceptText: payload.conceptText ?? null,
          definition: payload.definition,
        },
      });

      if (errors?.length > 0) {
        throw new Error(errors[0].message);
      }

      if (!data?.updateBusinessSemanticLayer) {
        throw new Error("Failed to update business definition");
      }

      return { success: true, data: data.updateBusinessSemanticLayer };
    } catch (error) {
      if (error.graphQLErrors?.length > 0) {
        throw new Error(error.graphQLErrors[0].message);
      }
      if (error.networkError?.result?.errors?.length > 0) {
        throw new Error(error.networkError.result.errors[0].message);
      }
      throw error;
    }
  }, [updateMutation]);

  /**
   * Deletes a business semantic layer by id.
   * @param {string|number} businessSemanticLayerId
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const deleteBusinessSemanticLayer = useCallback(async (businessSemanticLayerId) => {
    try {
      const { data, errors } = await deleteMutation({
        variables: { id: businessSemanticLayerId },
      });

      if (errors?.length > 0) {
        throw new Error(errors[0].message);
      }

      return { success: true, data: data?.deleteBusinessSemanticLayer };
    } catch (error) {
      console.error("Error deleting business semantic layer:", error);
      return { success: false, error: error?.message ?? "Failed to delete" };
    }
  }, [deleteMutation]);

  return {
    getBusinessSemanticLayers,
    addBusinessSemanticLayer,
    updateBusinessSemanticLayer,
    deleteBusinessSemanticLayer,
  };
};
